import { socials } from '@awpmarine/core/company';
import { LinkedInV2Logo } from '@awpmarine/ui/components/logos';
import { Badge } from '@awpmarine/ui/components/ui/badge';
import { Empty, EmptyContent, EmptyHeader, EmptyMedia, EmptyTitle } from '@awpmarine/ui/components/ui/empty';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { cn } from 'cn';
import { ArrowRightIcon, BriefcaseIcon, CheckIcon } from 'lucide-react';
import * as React from 'react';

import { Eyebrow, PageHero } from '@/components/marketing';
import DeckCrewImg from '@/images/marketing/deck-crew.jpg';
import { type RoleSummary, openRoleSummaries } from '@/lib/careers';
import { formatDate } from '@/lib/dates';
import { pageHead } from '@/lib/head';
import { ogImage } from '@/lib/open-graph';
import { m } from '@/paraglide/messages';
import type { MessageFunction } from '@/paraglide/runtime';

import { ApplicationForm, type InterestArea, interestAreas } from './-application-form';

export const metadata = () => ({ title: m.careers_title(), description: m.careers_description() });

export const Route = createFileRoute('/careers/')({
  // Filtered here rather than in render, so the browser hydrates with the prerender's list.
  loader: () => ({ roles: openRoleSummaries() }),
  head: () => pageHead({ ...metadata(), canonical: '/careers', image: ogImage('/careers') }),
  component: CareersPage,
});

const inspectors = {
  title: m.careers_look_title,
  items: [m.careers_look_certificate, m.careers_look_sea_time, m.careers_look_accreditation, m.careers_look_travel],
};

/** What each interest is asked for. Message functions, read when the page renders so the text follows its locale. */
const guidance: Record<InterestArea, { title: MessageFunction; items: MessageFunction[] }> = {
  'Inspector: tankers': inspectors,
  'Inspector: bulk carriers': inspectors,
  'Inspector: offshore and barges': inspectors,
  'Surveys and consultancy': {
    title: m.careers_look_title,
    items: [
      m.careers_surveyor_background,
      m.careers_surveyor_experience,
      m.careers_surveyor_reports,
      m.careers_surveyor_travel,
    ],
  },
  'Office role in the UK': {
    title: m.careers_look_title,
    items: [m.careers_office_experience, m.careers_office_detail, m.careers_office_english],
  },
  'Something else': {
    title: m.careers_other_title,
    items: [m.careers_other_role, m.careers_other_experience, m.careers_other_location],
  },
};

function CareersPage() {
  const [interest, setInterest] = React.useState<InterestArea>(interestAreas[0]);
  // Annotated because TypeScript 7 sometimes loses the loader's type in the cycle between a route and the route tree.
  const roles = Route.useLoaderData({ select: (data): RoleSummary[] => data.roles });

  return (
    <>
      <PageHero
        eyebrow={m.careers_eyebrow()}
        title={m.careers_heading()}
        lead={m.careers_lead()}
        image={{ src: DeckCrewImg, alt: m.careers_hero_image(), position: '50% 70%' }}
      />

      <section className={cn('mx-auto w-full max-w-310 px-5 sm:px-8', roles.length > 0 ? 'pb-16 sm:pb-22' : 'pb-6')}>
        <div className='mb-5 flex items-end justify-between gap-6'>
          <h2 className='text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
            {m.careers_open_roles()}
          </h2>
          {roles.length > 0 && <span className='text-subtle'>{m.careers_role_count({ count: roles.length })}</span>}
        </div>

        {roles.length > 0 ? (
          <>
            <div className='border-line border-b'>
              {roles.map((role) => (
                <Link
                  key={role.slug}
                  to='/careers/$slug'
                  params={{ slug: role.slug }}
                  className='group border-line flex flex-wrap items-center gap-x-8 gap-y-2.5 border-t py-6.5'
                >
                  <span className='min-w-0 flex-[1_1_380px] text-[21px] font-bold transition-colors group-hover:text-brand'>
                    {role.title}
                  </span>
                  <span className='flex flex-wrap gap-2'>
                    <Badge className='h-auto rounded-full bg-[#eef3fa] px-2.5 py-1 text-[13px] font-semibold text-[#1f4a80]'>
                      {role.type}
                    </Badge>
                    <Badge className='h-auto rounded-full bg-[#eef3fa] px-2.5 py-1 text-[13px] font-semibold text-[#1f4a80]'>
                      {role.location}
                    </Badge>
                    {role.pay && (
                      <Badge className='h-auto rounded-full bg-[#eef3fa] px-2.5 py-1 text-[13px] font-semibold text-[#1f4a80]'>
                        {role.pay}
                      </Badge>
                    )}
                  </span>
                  <span className='text-subtle text-sm whitespace-nowrap'>
                    {m.careers_role_dates({ posted: formatDate(role.posted), closes: formatDate(role.closes) })}
                  </span>
                  <span className='bg-mist hidden size-10 shrink-0 items-center justify-center rounded-full text-brand sm:flex'>
                    <ArrowRightIcon className='size-4.5' />
                  </span>
                </Link>
              ))}
            </div>
            <p className='text-body mt-6'>
              <ParaglideMessage
                message={m.careers_no_fit}
                inputs={{}}
                markup={{
                  link: ({ children }) => (
                    <a href='#interest' className='underline underline-offset-4 hover:text-brand'>
                      {children}
                    </a>
                  ),
                }}
              />
            </p>
          </>
        ) : (
          <Empty className='gap-3.5 rounded-3xl border border-[#cfd7e2] px-8 py-14'>
            <EmptyHeader className='max-w-none gap-3.5'>
              <EmptyMedia className='bg-mist mb-0 size-16 rounded-full text-brand'>
                <BriefcaseIcon className='size-7' />
              </EmptyMedia>
              <EmptyTitle className='mt-1.5 font-sans text-2xl font-bold tracking-normal'>
                <h3>{m.careers_none_title()}</h3>
              </EmptyTitle>
            </EmptyHeader>
            <EmptyContent className='mt-2.5 max-w-none flex-row flex-wrap justify-center gap-3 text-base'>
              <a
                href={socials.linkedin.url}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex min-h-12 items-center gap-2.5 rounded-full bg-[#0a66c2] px-6.5 font-semibold text-white'
              >
                <LinkedInV2Logo className='size-4.5' />
                {m.careers_follow_linkedin()}
              </a>
              <a
                href='#interest'
                className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full border border-[#cfd7e2] bg-white px-7 font-semibold transition-colors hover:border-brand hover:text-brand'
              >
                {m.careers_register()}
              </a>
            </EmptyContent>
          </Empty>
        )}
      </section>

      <section
        id='interest'
        className='mx-auto flex w-full max-w-310 scroll-mt-6 flex-wrap items-start gap-12 px-5 py-16 sm:px-8 sm:py-24'
      >
        <div className='min-w-0 flex-[1_1_340px]'>
          <Eyebrow>{m.careers_interest_eyebrow()}</Eyebrow>
          <h2 className='mb-7 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
            {m.careers_interest_title()}
          </h2>
          <div className='bg-mist rounded-[20px] p-6'>
            <h3 className='mb-3.5 font-bold'>{guidance[interest].title()}</h3>
            <ul className='text-body flex flex-col gap-3 leading-normal'>
              {guidance[interest].items.map((item) => (
                <li key={item()} className='flex items-start gap-2.5'>
                  <CheckIcon className='mt-0.5 size-4.5 shrink-0 text-brand' />
                  {item()}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <ApplicationForm onInterestChange={setInterest} className='bg-mist min-w-0 flex-[1.4_1_520px]' />
      </section>
    </>
  );
}
